import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Phone, Lock, Eye, EyeOff, ShieldCheck, Users, Sparkles, CheckCircle, UserPlus, ArrowRight } from 'lucide-react';

const TailorLogin = () => {
  const navigate = useNavigate();
  const { loginPhone, registerTailor } = useAuth();
  const [loginMethod, setLoginMethod] = useState('otp');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'register'
  const [formData, setFormData] = useState({
    mobile: '',
    otp: '',
    password: '',
    name: '',
    city: '',
    speciality: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const trustBadges = [
    { icon: Sparkles, label: 'AI Accuracy' },
    { icon: Users, label: 'Verified Tailors' },
    { icon: CheckCircle, label: 'Easy Alteration' },
    { icon: ShieldCheck, label: 'Secure' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSendOTP = () => {
    if (!formData.mobile || formData.mobile.length < 10) {
      setErrors({ mobile: 'Enter valid mobile number' });
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('otp');
    }, 1000);
  };

  const handleVerifyOTP = () => {
    if (!formData.otp || formData.otp.length < 4) {
      setErrors({ otp: 'Enter valid OTP' });
      return;
    }
    setIsLoading(true);
    // OTP verified → login via phone+password (default password = phone)
    loginPhone(formData.mobile, formData.mobile)
      .then(() => navigate('/tailor-dashboard'))
      .catch(() => {
        setErrors({ otp: 'Login failed. Try password login.' });
        setIsLoading(false);
      });
  };

  const handlePasswordLogin = async () => {
    if (!formData.mobile || formData.mobile.length < 10) {
      setErrors({ mobile: 'Enter valid mobile number' });
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setErrors({ password: 'Password must be at least 6 characters' });
      return;
    }
    setIsLoading(true);
    try {
      await loginPhone(formData.mobile, formData.password);
      navigate('/tailor-dashboard');
    } catch (err) {
      setErrors({ password: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.mobile || formData.mobile.length < 10) newErrors.mobile = 'Enter valid mobile number';
    if (!formData.city) newErrors.city = 'City is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    try {
      await registerTailor({
        name: formData.name,
        phone: formData.mobile,
        city: formData.city,
        speciality: formData.speciality,
        password: formData.mobile, // default password = phone
      });
      navigate('/tailor-dashboard');
    } catch (err) {
      setErrors({ mobile: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    if (step === 'otp') return 'Verify OTP';
    if (step === 'register') return 'Register as Tailor';
    return 'Welcome Back';
  };

  const getSubtitle = () => {
    if (step === 'otp') return `Enter OTP sent to ${formData.mobile}`;
    if (step === 'register') return 'Join Lunora\'s network of expert tailors';
    return 'Sign in to access your dashboard';
  };

  return (
    <div 
      data-testid="tailor-login"
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}
    >
      {/* Header */}
      <div 
        className="sticky top-0 z-50 flex items-center justify-between px-4 py-4 border-b"
        style={{ backgroundColor: 'rgba(250, 249, 246, 0.9)', backdropFilter: 'blur(12px)', borderColor: '#E5E5E5' }}
      >
        <button 
          data-testid="back-btn" 
          onClick={() => {
            if (step === 'otp') setStep('phone');
            else if (step === 'register') setStep('phone');
            else navigate('/landing');
          }} 
          className="p-2 -ml-2"
        >
          <ChevronLeft className="w-6 h-6" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Tailor Portal</h1>
        <div className="w-10" />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 py-8">
        {/* Logo & Welcome */}
        <div className="text-center mb-10">
          <img
            data-testid="tailor-login-logo"
            src="/lunora_logo.png"
            alt="Lunora's"
            className="w-32 h-auto mx-auto mb-4"
            style={{ display: 'block' }}
          />
          <h2 className="text-3xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
            {getTitle()}
          </h2>
          <p className="text-sm" style={{ color: '#595959' }}>
            {getSubtitle()}
          </p>
        </div>

        {/* Forms */}
        <div className="max-w-sm mx-auto w-full">

          {/* === PHONE / LOGIN STEP === */}
          {step === 'phone' && (
            <>
              {/* Method Toggle */}
              <div className="flex mb-6">
                <button
                  data-testid="otp-tab-btn"
                  onClick={() => setLoginMethod('otp')}
                  className={`flex-1 py-2 text-xs tracking-[0.1em] uppercase transition-colors ${
                    loginMethod === 'otp' ? 'text-white' : 'text-[#595959] bg-white border'
                  }`}
                  style={{ 
                    background: loginMethod === 'otp' ? '#0F0F0F' : undefined,
                    borderColor: '#E5E5E5'
                  }}
                >
                  OTP Login
                </button>
                <button
                  data-testid="password-tab-btn"
                  onClick={() => setLoginMethod('password')}
                  className={`flex-1 py-2 text-xs tracking-[0.1em] uppercase transition-colors ${
                    loginMethod === 'password' ? 'text-white' : 'text-[#595959] bg-white border'
                  }`}
                  style={{ 
                    background: loginMethod === 'password' ? '#0F0F0F' : undefined,
                    borderColor: '#E5E5E5'
                  }}
                >
                  Password
                </button>
              </div>

              {/* Mobile Input */}
              <div className="mb-6">
                <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                  <input
                    data-testid="mobile-input"
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    maxLength={10}
                    className="w-full pl-8 pr-0 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                    style={{ borderColor: errors.mobile ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                  />
                </div>
                {errors.mobile && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.mobile}</p>}
              </div>

              {/* Password Input (if password method) */}
              {loginMethod === 'password' && (
                <div className="mb-6">
                  <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                    <input
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password"
                      className="w-full pl-8 pr-10 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                      style={{ borderColor: errors.password ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 p-1">
                      {showPassword ? <EyeOff className="w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} /> : <Eye className="w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.password}</p>}
                  <div className="text-right mt-2">
                    <button type="button" className="text-xs" style={{ color: '#D4AF37' }}>Forgot Password?</button>
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                data-testid="login-submit-btn"
                onClick={loginMethod === 'otp' ? handleSendOTP : handlePasswordLogin}
                disabled={isLoading}
                className="w-full py-4 text-white text-sm tracking-[0.1em] uppercase transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
              >
                {isLoading ? 'Please wait...' : loginMethod === 'otp' ? 'Send OTP' : 'Sign In'}
              </button>

              {/* Register Link */}
              <div className="mt-8 text-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-[1px]" style={{ backgroundColor: '#E5E5E5' }} />
                  <span className="text-xs tracking-[0.1em] uppercase" style={{ color: '#BFBFBF' }}>or</span>
                  <div className="flex-1 h-[1px]" style={{ backgroundColor: '#E5E5E5' }} />
                </div>
                <button
                  data-testid="register-tailor-btn"
                  onClick={() => setStep('register')}
                  className="inline-flex items-center gap-2 py-3 px-6 text-sm tracking-[0.05em] transition-all border"
                  style={{ color: '#0F0F0F', borderColor: '#E5E5E5' }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#D4AF37';
                    e.currentTarget.style.color = '#D4AF37';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#E5E5E5';
                    e.currentTarget.style.color = '#0F0F0F';
                  }}
                >
                  <UserPlus className="w-4 h-4" strokeWidth={1.5} />
                  Register as Tailor
                  <ArrowRight className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            </>
          )}

          {/* === OTP STEP === */}
          {step === 'otp' && (
            <>
              <div className="mb-6">
                <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                  Enter OTP
                </label>
                <input
                  data-testid="otp-input"
                  type="text"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="w-full px-0 py-3 text-2xl text-center tracking-[0.5em] bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                  style={{ borderColor: errors.otp ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                />
                {errors.otp && <p className="text-xs mt-2 text-center" style={{ color: '#991B1B' }}>{errors.otp}</p>}
              </div>

              <button className="text-xs mb-6 block mx-auto" style={{ color: '#D4AF37' }}>
                Resend OTP
              </button>

              <button
                data-testid="verify-otp-btn"
                onClick={handleVerifyOTP}
                disabled={isLoading}
                className="w-full py-4 text-white text-sm tracking-[0.1em] uppercase transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
              >
                {isLoading ? 'Verifying...' : 'Verify & Login'}
              </button>
            </>
          )}

          {/* === REGISTER STEP === */}
          {step === 'register' && (
            <>
              {/* Name Input */}
              <div className="mb-5">
                <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                  Full Name
                </label>
                <input
                  data-testid="register-name-input"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                  style={{ borderColor: errors.name ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                />
                {errors.name && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.name}</p>}
              </div>

              {/* Mobile Input */}
              <div className="mb-5">
                <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#595959' }} strokeWidth={1.5} />
                  <input
                    data-testid="register-mobile-input"
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    maxLength={10}
                    className="w-full pl-8 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                    style={{ borderColor: errors.mobile ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                  />
                </div>
                {errors.mobile && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.mobile}</p>}
              </div>

              {/* City Input */}
              <div className="mb-5">
                <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                  City
                </label>
                <input
                  data-testid="register-city-input"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter your city"
                  className="w-full py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                  style={{ borderColor: errors.city ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                />
                {errors.city && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.city}</p>}
              </div>

              {/* Speciality */}
              <div className="mb-6">
                <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: '#595959' }}>
                  Speciality (Optional)
                </label>
                <select
                  data-testid="register-speciality-select"
                  name="speciality"
                  value={formData.speciality}
                  onChange={handleChange}
                  className="w-full py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F] appearance-none"
                  style={{ borderColor: '#E5E5E5', color: formData.speciality ? '#0F0F0F' : '#999' }}
                >
                  <option value="">Select speciality</option>
                  <option value="ethnic-women">Women's Ethnic Wear</option>
                  <option value="ethnic-men">Men's Ethnic Wear</option>
                  <option value="formal">Formal Wear</option>
                  <option value="casual">Casual Wear</option>
                  <option value="bridal">Bridal & Wedding</option>
                  <option value="all">All Categories</option>
                </select>
              </div>

              {/* Register Button */}
              <button
                data-testid="register-submit-btn"
                onClick={handleRegister}
                disabled={isLoading}
                className="w-full py-4 text-white text-sm tracking-[0.1em] uppercase transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
              >
                {isLoading ? 'Please wait...' : 'Register & Verify'}
              </button>

              {/* Back to login */}
              <div className="mt-6 text-center">
                <button
                  data-testid="back-to-login-btn"
                  onClick={() => setStep('phone')}
                  className="text-xs"
                  style={{ color: '#595959' }}
                >
                  Already have an account? <span style={{ color: '#D4AF37' }}>Sign In</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Trust Badges */}
        <div className="mt-12 grid grid-cols-4 gap-2 max-w-sm mx-auto">
          {trustBadges.map((badge, index) => (
            <div key={index} className="text-center">
              <badge.icon className="w-5 h-5 mx-auto mb-1" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <p className="text-[9px]" style={{ color: '#595959' }}>{badge.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TailorLogin;
